import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, CalendarCheck, ChevronDown, BookOpen } from 'lucide-react';
import { guides } from '../data/guides';
import { Button } from './ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Motorhomes', path: '/motorhomes' },
    { name: 'Servicing', path: '/servicing' },
    { name: 'Roller Team Parts', path: '/roller-team-parts' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'How it Works', path: '/how-it-works' },
    { name: 'Gift Vouchers', path: '/gift-vouchers' },
    { name: 'Guides & Tips', path: '/guides', children: guides.map((g) => ({ name: g.title, path: `/guides/${g.slug}`, category: g.category })) },
    { name: 'FAQs', path: '/faqs' }
  ];
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 px-4 text-sm">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <div className="text-xs md:text-sm">
            10% discount for serving/retired emergency services & armed forces
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:07527006966" className="flex items-center gap-1 hover:text-blue-300 transition-colors">
              <Phone className="h-3 w-3" />
              <span className="text-xs">07527006966</span>
            </a>
            <a href="https://www.facebook.com/100432836071005" target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 transition-colors">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a href="https://www.instagram.com/millies_motorhome_hire/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 transition-colors">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 bg-white shadow-md transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/01cb8dc0_Screenshot%202026-01-10%20185208.png" 
                alt="Millie's Motorhome Hire" 
                className="h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => link.children ? (
                <div key={link.path} className="relative group" data-testid="nav-guides-menu">
                  <Link
                    to={link.path}
                    className={`text-sm font-medium transition-colors hover:text-blue-600 flex items-center gap-1 py-2 ${
                      location.pathname.startsWith(link.path) ? 'text-blue-600 font-semibold' : 'text-slate-700'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-80 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                      <Link to={link.path} className="flex items-center gap-2 px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-900 hover:text-blue-700 border-b" data-testid="nav-guides-all">
                        <BookOpen className="h-4 w-4 text-orange-500" /> All guides &amp; tips
                      </Link>
                      <ul className="py-2">
                        {link.children.map((c) => (
                          <li key={c.path}>
                            <Link to={c.path} className={`block px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 ${location.pathname === c.path ? 'text-blue-700 font-semibold' : 'text-slate-700'}`}>
                              <span className="block text-[11px] uppercase tracking-wide text-slate-400">{c.category}</span>
                              {c.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                    location.pathname === link.path
                      ? 'text-blue-600 font-semibold'
                      : 'text-slate-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex gap-2">
                <Button asChild className="bg-orange-500 hover:bg-orange-600 font-bold shadow-md">
                  <Link to="/#book-online" data-testid="nav-book-online-btn">
                    <CalendarCheck className="mr-2 h-4 w-4" />
                    Book Online
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700">
                  <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer">
                    <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </a>
                </Button>
                <Button asChild className="bg-blue-600 hover:bg-blue-700">
                  <a href="mailto:milliesmotorhomehire@gmail.com">
                    <Mail className="mr-2 h-4 w-4" />
                    Email Us
                  </a>
                </Button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2">
              {navLinks.map((link) => link.children ? (
                <div key={link.path}>
                  <button
                    type="button"
                    onClick={() => setMobileGuidesOpen((o) => !o)}
                    className={`w-full flex items-center justify-between py-2 px-4 rounded-md transition-colors ${location.pathname.startsWith(link.path) ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-slate-700 hover:bg-gray-50'}`}
                    data-testid="mobile-nav-guides-toggle"
                  >
                    {link.name}
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileGuidesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileGuidesOpen && (
                    <div className="ml-4 mt-1 border-l-2 border-blue-100 pl-3 space-y-1">
                      <Link to={link.path} onClick={() => setIsOpen(false)} className="block py-1.5 px-2 text-sm font-semibold text-slate-900">All guides &amp; tips</Link>
                      {link.children.map((c) => (
                        <Link key={c.path} to={c.path} onClick={() => setIsOpen(false)} className="block py-1.5 px-2 text-sm text-slate-600 hover:text-blue-700">{c.name}</Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 px-4 rounded-md transition-colors ${
                    location.pathname === link.path
                      ? 'bg-blue-50 text-blue-600 font-semibold'
                      : 'text-slate-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Button asChild className="w-full bg-orange-500 hover:bg-orange-600 font-bold mt-4">
                <Link to="/#book-online" onClick={() => setIsOpen(false)} data-testid="mobile-nav-book-online-btn">
                  <CalendarCheck className="mr-2 h-4 w-4" />
                  Check Prices & Book Online
                </Link>
              </Button>
              <Button asChild className="w-full bg-green-600 hover:bg-green-700 mt-4">
                <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer">
                  <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
              </Button>
              <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 mt-4">
                <a href="mailto:milliesmotorhomehire@gmail.com">
                  <Mail className="mr-2 h-4 w-4" />
                  Email Us
                </a>
              </Button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
